/**
 * Generate a unique tenant ID using cryptographically secure random characters
 * Uses URL-safe and unambiguous characters
 */
export function generateTenantId(length: number = 8): string {
    // Use a mix of characters that are URL-safe and unambiguous
    const charset = "23456789ABCDEFGHJKLMNPQRSTWXYZabcdefghijkmnopqrstuvwxyz";
    let result = "";
    
    // Use Web Crypto API for browser compatibility
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
        result += charset[randomValues[i] % charset.length];
    }
    return result;
}

/**
 * Create a display name from an email address
 * Extracts the part before the @ symbol
 */
export function createDisplayNameFromEmail(email: string): string {
    if (!email) return 'User';
    
    // Get the part before the @ symbol
    const namePart = email.split('@')[0];
    
    // Capitalize the first letter and replace dots/underscores with spaces
    return namePart
        .replace(/[._]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Legacy function to generate tenant ID from first and last name
 * Kept for backward compatibility
 */
export function generateTenantIdFromName(firstName: string, lastName: string): string {
    return generateTenantId(8);
}

/**
 * Parse contact person string into first and last name
 */
export function parseContactPerson(contactPerson: string): { firstName: string; lastName: string } {
    if (!contactPerson) {
        return { firstName: '', lastName: '' };
    }
    
    const parts = contactPerson.trim().split(' ');
    if (parts.length === 1) {
        return { firstName: parts[0], lastName: '' };
    }
    
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    
    return { firstName, lastName };
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format based on length
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    return phone;
}

/**
 * Validate phone number
 */
export function isValidPhoneNumber(phone: string): boolean {
    if (!phone) return false;
    
    // Simple validation - at least 10 digits
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
}
/**
 * Create a tenant name from a tenant ID
 */
export function createTenantNameFromId(tenantId: string): string {
    if (!tenantId) return 'Your Company';
    
    // Format tenant ID by removing the last 4 characters and the preceding dash
    const dashIndex = tenantId.lastIndexOf('-');
    if (dashIndex !== -1 && tenantId.length - dashIndex === 5) {
        // Return only the part before the dash, with spaces instead of dashes
        return tenantId.substring(0, dashIndex).replace(/-/g, ' ');
    }
    
    // Otherwise, just replace dashes with spaces and capitalize each word
    return tenantId
        .replace(/-/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
}
/**
 * Generate a tenant ID from an organization name
 * Legacy function kept for backward compatibility
 */
export function generateTenantIdFromOrganization(organizationName: string): string {
    return generateTenantId(8);
}
